/**
 * AXEL Quant Lab (Beta) — admin/test-user only forecasting panel.
 *
 * Visual language matches Daily Brief / Research Desk (T.* palette,
 * Barlow Condensed labels). The underlying engine name is intentionally
 * NEVER surfaced — everything is branded "AXEL Quant Lab".
 */
import React, { useState } from "react";

const T = {
  bg: "#07090f", surf: "#0d1117", border: "#1c232f",
  white: "#e6e7ec", muted: "#878a91", gold: "#F5A623",
  green: "#22c55e", red: "#ef4444", blue: "#3b82f6",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSETS = [
  { id: "BTCUSD", label: "BTCUSD", icon: "₿" },
  { id: "XAUUSD", label: "XAUUSD", icon: "🥇" },
];
const TIMEFRAMES = ["15m", "1H", "4H"];

function _biasColor(bias) {
  if (bias === "Bullish") return T.green;
  if (bias === "Bearish") return T.red;
  return T.muted;
}
function _actionColor(action) {
  if (action === "Buy")  return T.green;
  if (action === "Sell") return T.red;
  return T.gold;
}
function _fmt(val, asset) {
  if (typeof val !== "number") return "—";
  const digits = asset === "BTCUSD" ? 0 : 2;
  return val.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

// ── A single label/value row in the result card ──────────────────
function Row({ label, children, mono = true, testId }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "8px 0", borderBottom: `1px dashed ${T.border}` }}>
      <span style={{ fontSize: ".62rem", color: T.muted, letterSpacing: ".12em", textTransform: "uppercase", fontFamily: "Barlow Condensed, sans-serif" }}>{label}</span>
      <span data-testid={testId} style={{ fontFamily: mono ? "'IBM Plex Mono', monospace" : "Barlow, sans-serif", fontSize: ".82rem", color: T.white, fontWeight: 600 }}>
        {children}
      </span>
    </div>
  );
}

export default function QuantLabTab() {
  const [asset, setAsset] = useState("BTCUSD");
  const [timeframe, setTimeframe] = useState("1H");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const runForecast = async () => {
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await fetch(`${API}/axel/quant-lab/forecast`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ asset, timeframe }),
      });
      if (!r.ok) throw new Error("unavailable");
      const data = await r.json();
      setResult(data);
    } catch (e) {
      setError("AXEL Quant Lab is temporarily unavailable. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  const hasData = result && result.action !== "Wait" && result.confidence > 0;
  const dataMissing = result && result.confidence === 0;

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "8px 18px 40px" }} data-testid="quant-lab-tab">
      {/* Title row with BETA badge */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <h2 style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: "1.35rem", fontWeight: 800, color: T.white, margin: 0, letterSpacing: ".02em" }}>
              AXEL Quant Lab
            </h2>
            <span data-testid="quant-lab-beta-badge" style={{
              fontFamily: "Barlow Condensed, sans-serif", fontSize: ".58rem", fontWeight: 800, letterSpacing: ".18em",
              padding: "3px 8px", borderRadius: 4, background: T.gold, color: "#07090f",
            }}>BETA</span>
          </div>
          <div style={{ fontSize: ".62rem", color: T.muted, letterSpacing: ".06em", fontFamily: "Barlow, sans-serif", maxWidth: 540, lineHeight: 1.55 }}>
            Experimental probabilistic forecast for testing and education only. Not financial advice.
          </div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ background: T.surf, border: `1px solid ${T.border}`, borderRadius: 10, padding: 16, marginBottom: 14 }}>
        <div style={{ fontSize: ".58rem", color: T.muted, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 10, fontFamily: "Barlow Condensed, sans-serif" }}>Asset</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {ASSETS.map(a => {
            const active = a.id === asset;
            return (
              <button
                key={a.id}
                data-testid={`quant-asset-${a.id}`}
                onClick={() => setAsset(a.id)}
                style={{
                  padding: "8px 14px", borderRadius: 6, cursor: "pointer",
                  background: active ? "#1a1407" : T.bg,
                  border: `1px solid ${active ? T.gold : T.border}`,
                  color: active ? T.gold : T.muted,
                  fontFamily: "Barlow Condensed, sans-serif", fontSize: ".85rem", fontWeight: 700, letterSpacing: ".06em",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              ><span>{a.icon}</span>{a.label}</button>
            );
          })}
        </div>

        <div style={{ fontSize: ".58rem", color: T.muted, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 10, fontFamily: "Barlow Condensed, sans-serif" }}>Timeframe</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          {TIMEFRAMES.map(tf => {
            const active = tf === timeframe;
            return (
              <button
                key={tf}
                data-testid={`quant-tf-${tf}`}
                onClick={() => setTimeframe(tf)}
                style={{
                  padding: "8px 16px", borderRadius: 6, cursor: "pointer",
                  background: active ? "#1a1407" : T.bg,
                  border: `1px solid ${active ? T.gold : T.border}`,
                  color: active ? T.gold : T.muted,
                  fontFamily: "Barlow Condensed, sans-serif", fontSize: ".85rem", fontWeight: 700, letterSpacing: ".06em",
                }}
              >{tf}</button>
            );
          })}
        </div>

        <button
          data-testid="quant-run-forecast"
          onClick={runForecast}
          disabled={loading}
          style={{
            width: "100%", padding: "12px 16px", borderRadius: 6, cursor: loading ? "not-allowed" : "pointer",
            background: loading ? T.border : "linear-gradient(135deg,#F5A623,#c97d10)",
            border: "none", color: "#07090f",
            fontFamily: "Barlow Condensed, sans-serif", fontSize: ".95rem", fontWeight: 800, letterSpacing: ".1em",
            textTransform: "uppercase",
          }}
        >
          {loading ? "Analyzing market structure…" : "Run Forecast"}
        </button>
      </div>

      {/* Loading hint */}
      {loading && (
        <div style={{ background: T.surf, border: `1px solid ${T.border}`, borderRadius: 10, padding: 18, marginBottom: 14, textAlign: "center" }}>
          <div style={{ fontSize: ".75rem", color: T.gold, fontFamily: "Barlow Condensed, sans-serif", letterSpacing: ".08em", marginBottom: 4 }}>
            AXEL Quant Lab is analyzing recent market structure…
          </div>
          <div style={{ fontSize: ".6rem", color: T.muted, fontFamily: "Barlow, sans-serif" }}>
            Typically completes within a few seconds.
          </div>
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div data-testid="quant-error" style={{ background: "#2a0a0a", border: `1px solid ${T.red}40`, borderRadius: 10, padding: 14, marginBottom: 14, color: T.red, fontSize: ".75rem", fontFamily: "Barlow, sans-serif" }}>
          {error}
        </div>
      )}

      {/* Result card */}
      {result && !loading && (
        <div data-testid="quant-result-card" style={{ background: T.surf, border: `1px solid ${T.border}`, borderRadius: 10, padding: 18 }}>
          {/* Headline: Asset + Bias + Action */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${T.border}` }}>
            <div>
              <div style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: "1.05rem", fontWeight: 800, color: T.white, letterSpacing: ".04em" }}>
                {result.asset} · {result.timeframe}
              </div>
              <div style={{ fontSize: ".58rem", color: T.muted, fontFamily: "Barlow, sans-serif", marginTop: 2 }}>
                Forecast horizon: {result.forecast_horizon}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span data-testid="quant-bias" style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: ".85rem", fontWeight: 800, color: _biasColor(result.bias), letterSpacing: ".06em" }}>
                {result.bias?.toUpperCase()}
              </span>
              <span data-testid="quant-action" style={{
                padding: "5px 12px", borderRadius: 4,
                background: _actionColor(result.action) + "20",
                color: _actionColor(result.action),
                fontFamily: "Barlow Condensed, sans-serif", fontSize: ".85rem", fontWeight: 800, letterSpacing: ".1em",
                border: `1px solid ${_actionColor(result.action)}50`,
              }}>{result.action?.toUpperCase()}</span>
            </div>
          </div>

          {/* If data missing, show only the note */}
          {dataMissing ? (
            <div style={{ color: T.muted, fontSize: ".78rem", fontFamily: "Barlow, sans-serif", lineHeight: 1.6 }}>
              {result.axel_note}
            </div>
          ) : (
            <>
              <Row label="Execution Timeframe" testId="quant-exec-tf">{result.execution_timeframe}</Row>
              {hasData && (
                <>
                  <Row label="Entry Zone" testId="quant-entry">
                    {_fmt(result.entry_zone?.low, result.asset)} – {_fmt(result.entry_zone?.high, result.asset)}
                  </Row>
                  <Row label="Take Profit 1" testId="quant-tp1">{_fmt(result.take_profit?.tp1, result.asset)}</Row>
                  <Row label="Take Profit 2" testId="quant-tp2">{_fmt(result.take_profit?.tp2, result.asset)}</Row>
                  <Row label="Stop / Invalidation" testId="quant-sl">{_fmt(result.stop_loss, result.asset)}</Row>
                </>
              )}
              <Row label="Confidence" testId="quant-confidence">
                <span style={{ color: result.confidence >= 60 ? T.green : result.confidence >= 40 ? T.gold : T.muted }}>
                  {result.confidence}%
                </span>
              </Row>
              <Row label="Projected Range" testId="quant-range">
                {_fmt(result.projected_range?.low, result.asset)} – {_fmt(result.projected_range?.high, result.asset)}
              </Row>
              <Row label="Live Data" mono={false} testId="quant-live-ts">
                {result.live_data_timestamp ? new Date(result.live_data_timestamp).toLocaleString() : "—"}
              </Row>
              <Row label="Generated" mono={false} testId="quant-generated">
                {result.generated_at ? new Date(result.generated_at).toLocaleString() : "—"}
              </Row>

              {/* AXEL Note */}
              <div style={{ marginTop: 14, padding: 12, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6 }}>
                <div style={{ fontSize: ".58rem", color: T.gold, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 6, fontFamily: "Barlow Condensed, sans-serif" }}>
                  AXEL Note
                </div>
                <div data-testid="quant-axel-note" style={{ fontSize: ".75rem", color: T.white, lineHeight: 1.6, fontFamily: "Barlow, sans-serif" }}>
                  {result.axel_note}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Footer disclaimer */}
      <div style={{ marginTop: 18, fontSize: ".58rem", color: T.muted, fontFamily: "Barlow, sans-serif", lineHeight: 1.6, textAlign: "center" }}>
        AXEL Quant Lab is in private beta. Output is probabilistic and may be wrong.
        Use only as a decision-support signal alongside your own analysis.
      </div>
    </div>
  );
}
